export type LoadingPhase = 'idle' | 'engine' | 'manifests' | 'models' | 'preparing' | 'ready' | 'error'
export type LoadingFailureCode =
  | 'RESOURCE_LOAD_FAILED'
  | 'MODEL_PARSE_FAILED'
  | 'ASSET_LOAD_FAILED'
  | 'ENGINE_INITIALIZATION_FAILED'
  | 'SCENE_INITIALIZATION_FAILED'

export interface LoadingSnapshot {
  phase: LoadingPhase
  loadedBytes: number
  totalBytes: number
  loadedModels: number
  totalModels: number
  pendingResources: number
  /** Safe UI classification only; never contains the original exception text. */
  error: LoadingFailureCode | null
}

let snapshot: LoadingSnapshot = {
  phase: 'idle', loadedBytes: 0, totalBytes: 0, loadedModels: 0,
  totalModels: 0, pendingResources: 0, error: null,
}
const listeners = new Set<() => void>()
let firstFailure: { phase: LoadingPhase; error: unknown } | null = null
const modelBytes = new Map<string, { bytes: number; loaded: number; complete: boolean }>()
let pendingResources = 0
let updateTimer: ReturnType<typeof setTimeout> | undefined

export const getLoadingSnapshot = () => snapshot
export function subscribeLoading(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

function publish(patch: Partial<LoadingSnapshot>) {
  snapshot = { ...snapshot, ...patch }
  listeners.forEach((listener) => listener())
}

export function setLoadingPhase(phase: LoadingPhase) {
  if (snapshot.phase === 'error') return
  publish({ phase })
}

function errorDetails(error: unknown) {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  return typeof error === 'string' ? error : ''
}

/** Classifies by load phase and known loader signals without exposing details to the UI. */
export function classifyLoadingFailure(error: unknown, phase: LoadingPhase): LoadingFailureCode {
  const details = errorDetails(error)
  if (/asset could not be loaded|failed to fetch|networkerror|load failed|returned http \d{3}|http status(?: code)?[: ]+\d{3}/i.test(details)) {
    return 'RESOURCE_LOAD_FAILED'
  }
  if (((phase === 'models' || phase === 'preparing') && error instanceof SyntaxError) || /invalid gltf|malformed gltf|failed to parse gltf|gltf parse error|unsupported asset.{0,80}gltf|meshopt.{0,40}(?:malformed|invalid|decode error)|(?:malformed|invalid).{0,40}meshopt/i.test(details)) {
    return 'MODEL_PARSE_FAILED'
  }

  if (phase === 'engine') return 'ENGINE_INITIALIZATION_FAILED'
  if (phase === 'models') return 'ASSET_LOAD_FAILED'

  return 'SCENE_INITIALIZATION_FAILED'
}

export function failLoading(error: unknown, phase: LoadingPhase = snapshot.phase, componentStack?: string | null) {
  // Keep the earliest failure as the actionable cause; later cleanup/rejection
  // errors must not replace it or obscure its stage.
  if (firstFailure) return
  firstFailure = { phase, error }
  const errorCode = classifyLoadingFailure(error, phase)
  console.error(`[HumanMuseum] Failure during loading phase "${phase}" (${errorCode})`, error, componentStack ?? '')
  publish({ phase: 'error', error: errorCode })
}

export function planModels(models: Array<{ url: string; bytes: number }>) {
  for (const { url, bytes } of models) {
    if (!modelBytes.has(url)) modelBytes.set(url, { bytes, loaded: 0, complete: false })
  }
  publish({
    phase: 'models',
    totalBytes: [...modelBytes.values()].reduce((sum, model) => sum + model.bytes, 0),
    totalModels: modelBytes.size,
  })
}

function publishModels() {
  updateTimer = undefined
  const models = [...modelBytes.values()]
  const complete = models.filter((model) => model.complete).length
  const loadedBytes = models.reduce((sum, model) => sum + model.loaded, 0)
  publish({
    loadedBytes,
    loadedModels: complete,
    ...(snapshot.phase !== 'error' && snapshot.phase !== 'ready'
      && (complete === models.length || (snapshot.totalBytes > 0 && loadedBytes === snapshot.totalBytes))
      ? { phase: 'preparing' as const } : {}),
  })
}

/** Bytes are reported by the loader, with a fixed total from the built asset manifest. */
export function reportModelProgress(url: string, loaded: number, complete = false) {
  const model = modelBytes.get(url)
  if (!model) return
  model.loaded = complete ? model.bytes : Math.min(model.bytes, Math.max(model.loaded, loaded))
  model.complete ||= complete
  if (complete) {
    clearTimeout(updateTimer)
    publishModels()
  } else if (!updateTimer) updateTimer = setTimeout(publishModels, 100)
}

export function resourceStarted() {
  pendingResources += 1
  publish({ pendingResources })
}

export function resourceFinished() {
  pendingResources = Math.max(0, pendingResources - 1)
  publish({ pendingResources })
}

export const resourcesPending = () => pendingResources
